import axios, { AxiosResponse } from "axios";
import { ActivitySchema } from "@/models/activity_schema";
import { AuthError, APIError } from "@/services/error_service";
/**
 * Creates a new Activity Service

 * @example
 * const activityService = new ActivityService();
 * activityService.getActivity(id);
 */

 axios.defaults.withCredentials = true;
 axios.defaults.headers!.apikey = process.env.VUE_APP_API_KEY;

export default class ActivityService {
  /**
   * Returns request activity
   * @param {id} number - unique activity id
   * @returns {ActivitySchema} - response object with activity data
   */
  public async getActivity(id: string): Promise<ActivitySchema> {
    let activity;
    try {
      const options = {
        withCredentials: true,
      };
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/activities/" + id,
        options
      );
      activity = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        }
        else if (err.response.status === 404) {
          throw new APIError('The requested information was not found.')
        }
        else if (err.response.status === 403) {
          throw new APIError('You do not have access to the requested resource.')
        }
      }
      else {
        throw new Error('Something went wrong. Please try again.')
      }
    }
    return activity;
  }

  /**
   * Update activity
   * @param {ActivitySchema} request - new activity data object
   * @param {id} number - unique id of activity to be updated
   * @returns {ActivitySchema} - newly created activity object
   */
  public async updateActivity(
    request: ActivitySchema,
    id: string
  ): Promise<ActivitySchema> {
    let activity;
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      const res: AxiosResponse<any> = await axios.put(
        process.env.VUE_APP_API_URL + "/activities/" + id,
        body,
        options
      );
      activity = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        }
        else if (err.response.status === 404) {
          throw new APIError('The requested information was not found.')
        }
        else if (err.response.status === 403) {
          throw new APIError('You do not have access to the requested resource.')
        }
        else if (err.response.status === 400) {
          throw new APIError('Invalid request. Please try again.')
        }
      }
      else {
        throw new Error('Something went wrong. Please try again.')
      }
    }
    return activity;
  }

  /**
   * Delete activity
   * @param {number} id - activity id
   * @returns {Promise<void>}
   */
  public async deleteActivity(id: string): Promise<void> {
    try {
      const options = {
        withCredentials: true,
      };
      return await axios.delete(
        process.env.VUE_APP_API_URL + "/activities/" + id,
        options
      );
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        }
        else if (err.response.status === 404) {
          throw new APIError('The requested information was not found.')
        }
        else if (err.response.status === 403) {
          throw new APIError('You do not have access to the requested resource.')
        }
      }
      else {
        throw new Error('Something went wrong. Please try again.')
      }
    }
  }
}
